import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  TrendingUp, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  RefreshCw,
  Clock,
  DollarSign,
  Wrench,
  FileText,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Building2,
  Calendar
} from 'lucide-react';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import PHSCommandLayout from '@/components/phs/enterprise/PHSCommandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// Category colors
const CATEGORY_COLORS = {
  'Base Works': 'bg-amber-100 text-amber-800 border-amber-200',
  'External': 'bg-blue-100 text-blue-800 border-blue-200',
  'Utilities': 'bg-purple-100 text-purple-800 border-purple-200',
  'Internal': 'bg-green-100 text-green-800 border-green-200',
  'Compliance': 'bg-red-100 text-red-800 border-red-200',
  'Surveying': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'Other': 'bg-slate-100 text-slate-800 border-slate-200'
};

const CATEGORIES = ['Base Works', 'External', 'Utilities', 'Internal', 'Compliance', 'Surveying', 'Other'];

function BenchmarkingPage() {
  const navigate = useNavigate();
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState({});
  const [showDetail, setShowDetail] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBenchmark, setEditingBenchmark] = useState(null);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadBenchmarks();
  }, [categoryFilter, searchTerm]);
  
  const loadBenchmarks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await axios.get(`${API}/api/phs/benchmarks?${params}`, { headers });
      setBenchmarks(res.data.benchmarks || []);
      setCategories(res.data.categories || {});
    } catch (err) {
      console.error('Failed to load benchmarks:', err);
      toast.error('Failed to load benchmarks');
    } finally {
      setLoading(false);
    }
  };
  
  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/api/phs/benchmarks/seed-defaults`, {}, { headers });
      toast.success(res.data.message);
      loadBenchmarks();
    } catch (err) {
      console.error('Failed to seed benchmarks:', err);
      toast.error('Failed to seed default benchmarks');
    }
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(value);
  };
  
  // Filter benchmarks
  const filteredBenchmarks = benchmarks;
  
  return (
    <PHSCommandLayout>
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-teal-600" />
              Benchmarking
            </h1>
            <p className="text-sm text-slate-500 mt-1">Internal pricing intelligence for PHS operations</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadBenchmarks}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={seedDefaults}>
              Seed Defaults
            </Button>
            <Button size="sm" onClick={() => setShowCreateModal(true)} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-1" />
              Add Benchmark
            </Button>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search job types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="benchmark-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>
                  {cat} ({categories[cat] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat === categoryFilter ? 'all' : cat)}
            className={`p-3 rounded-xl border transition-all ${
              cat === categoryFilter 
                ? 'ring-2 ring-teal-500 border-teal-500' 
                : 'hover:border-slate-300'
            } ${CATEGORY_COLORS[cat]}`}
          >
            <div className="text-lg font-bold">{categories[cat] || 0}</div>
            <div className="text-xs truncate">{cat}</div>
          </button>
        ))}
      </div>
      
      {/* Benchmarks Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                <div className="h-4 bg-slate-200 rounded w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredBenchmarks.length === 0 ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Benchmarks Found</h3>
          <p className="text-sm text-slate-500 mb-4">
            {searchTerm || categoryFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Click "Seed Defaults" to add standard benchmark data'}
          </p>
          <Button onClick={seedDefaults}>Seed Default Benchmarks</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBenchmarks.map(bench => (
            <Card 
              key={bench.benchmark_id} 
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => setShowDetail(bench)}
              data-testid={`benchmark-card-${bench.benchmark_id}`}
            >
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <Badge className={`text-[10px] mb-1 ${CATEGORY_COLORS[bench.category]}`}>
                      {bench.category}
                    </Badge>
                    <h3 className="font-semibold text-slate-800 truncate">{bench.job_type}</h3>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-teal-500 transition" />
                </div>
                
                {/* Price Range */}
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase">Low</div>
                      <div className="text-sm font-semibold text-green-600">{formatCurrency(bench.typical_low)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase">Average</div>
                      <div className="text-sm font-bold text-slate-800">{formatCurrency(bench.average_cost)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase">High</div>
                      <div className="text-sm font-semibold text-amber-600">{formatCurrency(bench.typical_high)}</div>
                    </div>
                  </div>
                </div>
                
                {/* Duration & Labour */}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  {bench.average_duration_days && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{bench.average_duration_days} days</span>
                    </div>
                  )}
                  {bench.typical_labour && (
                    <div className="flex items-center gap-1">
                      <Wrench className="h-3 w-3" />
                      <span className="truncate">{bench.typical_labour}</span>
                    </div>
                  )}
                </div>
                
                {/* Scope Preview */}
                {bench.common_scope_items?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {bench.common_scope_items.slice(0, 3).map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-[10px]">
                        {item}
                      </Badge>
                    ))}
                    {bench.common_scope_items.length > 3 && (
                      <Badge variant="outline" className="text-[10px] text-slate-400">
                        +{bench.common_scope_items.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Detail Modal */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Badge className={CATEGORY_COLORS[showDetail.category]}>{showDetail.category}</Badge>
                {showDetail.job_type}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Description */}
              {showDetail.description && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Description</h4>
                  <p className="text-sm text-slate-600">{showDetail.description}</p>
                </div>
              )}
              
              {/* Pricing */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Pricing Range</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-xs text-green-600 uppercase">Low</div>
                    <div className="text-lg font-bold text-green-700">{formatCurrency(showDetail.typical_low)}</div>
                  </div>
                  <div className="bg-slate-100 p-3 rounded-lg text-center">
                    <div className="text-xs text-slate-500 uppercase">Average</div>
                    <div className="text-lg font-bold text-slate-800">{formatCurrency(showDetail.average_cost)}</div>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <div className="text-xs text-amber-600 uppercase">High</div>
                    <div className="text-lg font-bold text-amber-700">{formatCurrency(showDetail.typical_high)}</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-xs text-blue-600 uppercase">Duration</div>
                    <div className="text-lg font-bold text-blue-700">{showDetail.average_duration_days || '-'} days</div>
                  </div>
                </div>
              </div>
              
              {/* Labour */}
              {showDetail.typical_labour && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Typical Labour</h4>
                  <p className="text-sm text-slate-600">{showDetail.typical_labour}</p>
                </div>
              )}
              
              {/* Scope Items */}
              {showDetail.common_scope_items?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Common Scope Items</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {showDetail.common_scope_items.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Variations */}
              {showDetail.common_variations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Common Variations</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {showDetail.common_variations.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Follow-on Jobs */}
              {showDetail.common_follow_on_jobs?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Common Follow-On Jobs</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {showDetail.common_follow_on_jobs.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Evidence Required */}
              {showDetail.evidence_required?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Evidence Required</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {showDetail.evidence_required.map((item, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {showDetail.rate_notes && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Notes</h4>
                  <p className="text-sm text-slate-600">{showDetail.rate_notes}</p>
                </div>
              )}
            </div>
            
            <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
              <div className="flex flex-wrap gap-2 flex-1">
                <Button 
                  variant="outline"
                  onClick={() => {
                    const params = new URLSearchParams({
                      action: 'new-job',
                      prefill_category: showDetail.category,
                      prefill_type: showDetail.job_type,
                      prefill_estimate: showDetail.average_cost
                    });
                    navigate(`/phs/helpdesk?${params.toString()}`);
                  }}
                  className="text-teal-600 border-teal-200 hover:bg-teal-50"
                  data-testid="benchmark-create-job-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Job
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    toast.info(`Assign "${showDetail.job_type}" to Asset - Feature coming soon`);
                  }}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="benchmark-assign-asset-btn"
                >
                  <Building2 className="h-4 w-4 mr-1" />
                  Assign To Asset
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigate(`/phs/ppm/schedule?add_template=${showDetail.benchmark_id}&type=${encodeURIComponent(showDetail.job_type)}`);
                  }}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="benchmark-add-ppm-btn"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Add To PPM Schedule
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetail(null)}>Close</Button>
                <Button 
                  onClick={() => {
                    setEditingBenchmark(showDetail);
                    setShowDetail(null);
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="benchmark-edit-btn"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(BenchmarkingPage, 'helpdesk');
